import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, Sparkles, MousePointer, Waves, Eye, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEffectsStore } from '@/store/effects.store.ts';

interface EffectsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const EffectsPanel: React.FC<EffectsPanelProps> = ({
  isOpen,
  onClose
}) => {
  const {
    particlesEnabled,
    clickEffectsEnabled,
    mouseAttractionEnabled,
    waveEffectsEnabled,
    toggleParticles,
    toggleClickEffects,
    toggleMouseAttraction,
    toggleWaveEffects,
    setParticlesEnabled,
    setClickEffectsEnabled,
    setMouseAttractionEnabled,
    setWaveEffectsEnabled,
  } = useEffectsStore();

  // 每个 switch 与 store 一一对应：显示与操作均用同一字段
  const settings = [
    {
      id: 'particles',
      label: '粒子动画',
      description: '显示动态粒子背景效果',
      icon: Sparkles,
      enabled: particlesEnabled,
      disabled: false,
      toggle: toggleParticles,
    },
    {
      id: 'mouseAttraction',
      label: '鼠标吸引',
      description: '粒子围绕鼠标形成环状（需先开启粒子效果）',
      icon: MousePointer,
      enabled: mouseAttractionEnabled,
      disabled: !particlesEnabled,
      toggle: () => {
        if (particlesEnabled) toggleMouseAttraction();
        else toast.error('请先开启粒子动画效果');
      },
    },
    {
      id: 'clickEffects',
      label: '点击特效',
      description: '点击时产生水墨涟漪效果',
      icon: Eye,
      enabled: clickEffectsEnabled,
      disabled: false,
      toggle: toggleClickEffects,
    },
    {
      id: 'waveEffects',
      label: '波浪动画',
      description: '底部波浪起伏动画',
      icon: Waves,
      enabled: waveEffectsEnabled,
      disabled: false,
      toggle: toggleWaveEffects,
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
            onClick={onClose}
          />

          {/* 设置面板 */}
          <motion.div
            initial={{ opacity: 0, x: 400 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 400 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-80 bg-white dark:bg-gray-900 shadow-2xl z-[60] overflow-y-auto"
          >
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <SettingsIcon className="text-blue-500" size={20} />
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    特效设置
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    自定义视觉效果
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* 设置列表 */}
            <div className="p-6 space-y-6">
              <div className="border border-blue-200 dark:border-blue-800 rounded-xl p-4 bg-blue-50/60 dark:bg-blue-900/20 text-sm text-blue-900 dark:text-blue-100">
                当前的点赞、收藏、分享等互动功能已经收纳到左侧悬浮工具栏，如需操作请使用左侧按钮组。
              </div>

              {/* 特效设置 */}
              {settings.map((setting) => {
                const Icon = setting.icon;
                return (
                  <motion.div
                    key={setting.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex items-start space-x-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${
                      setting.enabled 
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                    }`}>
                      <Icon size={20} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {setting.label}
                        </h3>
                        <button
                          type="button"
                          onClick={setting.toggle}
                          disabled={setting.disabled}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            setting.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                          } ${setting.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <motion.span
                            animate={{ x: setting.enabled ? 20 : 2 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            className="inline-block h-4 w-4 rounded-full bg-white shadow-lg"
                          />
                        </button>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {setting.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}

              {/* 预设方案 */}
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                  特效预设
                </h3>
                
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setParticlesEnabled(true);
                      setClickEffectsEnabled(true);
                      setMouseAttractionEnabled(true);
                      setWaveEffectsEnabled(true);
                    }}
                    className="w-full p-3 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      🎨 完整体验
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      开启所有特效
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setParticlesEnabled(false);
                      setClickEffectsEnabled(false);
                      setMouseAttractionEnabled(false);
                      setWaveEffectsEnabled(false);
                    }}
                    className="w-full p-3 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      🚀 性能优先
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      关闭所有特效
                    </div>
                  </button>
                </div>
              </div>

              {/* 提示信息 */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 特效设置会自动保存，刷新页面后仍然有效
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EffectsPanel;
